import React from 'react';
import Grid from '@mui/material/Grid';
import { Paper, Typography, Box, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getIndustryName } from '../../utils/sicCodes';
import { toIndustrySlug } from '../../utils/industryRouting';
import { getGradeColor } from '../../utils/gradeColors';
import { formatCompanySizeLabel } from '../../utils/companySize';

const CompanyProfileCard: React.FC<{ company: any }> = ({ company }) => {
  const gradeColor = getGradeColor(company.LetterGrade);
  const latestYear = company.LatestReportingYear || 2023;
  const industryLabel = getIndustryName(company.FinalIndustryCode);
  const industrySlug = toIndustrySlug(company.FinalIndustryCode);

  const getLatestProp = (prop: string) => company[`${latestYear}_${prop}`] || company[prop] || 'N/A';

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2, borderTop: `4px solid ${gradeColor}` }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{company.CleanCompanyName}</Typography>
          {industrySlug ? (
            <MuiLink
              component={RouterLink}
              to={`/industry/${industrySlug}`}
              underline="hover"
              color="text.secondary"
              sx={{ fontSize: '1rem' }}
            >
              {industryLabel}
            </MuiLink>
          ) : (
            <Typography variant="body1" color="text.secondary">{industryLabel}</Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: gradeColor,
            color: 'white',
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0px 4px 12px rgba(78, 70, 229, 0.25)'
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{company.LetterGrade}</Typography>
        </Box>
      </Box>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary">Registered Address</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>{getLatestProp('Address')}</Typography>

          <Typography variant="subtitle2" color="text.secondary">Person Responsible</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>{getLatestProp('ResponsiblePerson')}</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">Composite Score</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{(company.FinalCompositeScore || 0).toFixed(1)}/100</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">Employees</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formatCompanySizeLabel(getLatestProp('EmployerSize'))}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">Trend Score</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{(company.TrendScore || 0).toFixed(1)}/100</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">Current Performance</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{(company.CurrentPerformanceScore || 0).toFixed(1)}/100</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">Date Submitted ({latestYear})</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{getLatestProp('DateSubmitted')}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">Due Date ({latestYear})</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{getLatestProp('DueDate')}</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CompanyProfileCard;
